import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";

const LINKS = [
    { label: "Studio", path: "/studio" },
    { label: "Services", path: "/services" },
    { label: "Portfolio", path: "/portfolio" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const go = (path) => {
        setOpen(false);
        navigate(path);
    };

    return (
        <>
            <motion.header
                data-testid="main-nav"
                className="fixed inset-x-0 top-4 z-[100] flex justify-center px-4"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 3.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
                <nav
                    className={`flex w-full max-w-4xl items-center justify-between rounded-full px-5 py-3 transition-[background-color,border-color,box-shadow] duration-500 ${
                        scrolled
                            ? "glass shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
                            : "border border-white/[0.06] bg-[#0B0B0B]/40 backdrop-blur-md"
                    }`}
                    aria-label="Primary"
                >
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="flex items-center gap-3"
                        aria-label="ate.teen studio — home"
                    >
                        <img
                            src="/images/logo.jpg"
                            alt="ate.teen studio logo"
                            className="h-9 w-9 rounded-lg border border-white/10 object-cover"
                        />
                        <span className="hidden font-display text-sm font-black lowercase tracking-[0.06em] text-white sm:block">
                            ate.teen
                        </span>
                    </Link>

                    <div className="hidden items-center gap-7 lg:flex">
                        {LINKS.map((link) => {
                            const active = location.pathname === link.path;
                            return (
                                <Link
                                    key={link.label}
                                    to={link.path}
                                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                                    className={`group relative text-[11px] font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
                                        active
                                            ? "text-gold"
                                            : "text-ash hover:text-white"
                                    }`}
                                >
                                    {link.label}
                                    <span
                                        className={`absolute -bottom-1 left-0 h-px w-full origin-left bg-gold transition-transform duration-500 ease-out ${
                                            active
                                                ? "scale-x-100"
                                                : "scale-x-0 group-hover:scale-x-100"
                                        }`}
                                    />
                                </Link>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-3">
                        <MagneticButton className="hidden sm:block">
                            <button
                                data-testid="nav-start-project-btn"
                                onClick={() => go("/contact")}
                                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-coal transition-colors duration-300 hover:bg-white"
                            >
                                Start Project
                                <ArrowUpRight size={14} />
                            </button>
                        </MagneticButton>
                        <button
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen(!open)}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white lg:hidden"
                            aria-label={open ? "Close menu" : "Open menu"}
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[95] flex flex-col justify-center bg-coal/95 px-8 backdrop-blur-xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="blueprint-grid absolute inset-0 opacity-40" />
                        <div className="relative flex flex-col gap-2">
                            {[
                                { label: "Home", path: "/" },
                                ...LINKS,
                                { label: "Contact", path: "/contact" },
                            ].map((link, i) => (
                                <div key={link.label} className="overflow-hidden">
                                    <motion.button
                                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                        onClick={() => go(link.path)}
                                        className={`block font-display text-5xl font-bold uppercase tracking-tight ${
                                            location.pathname === link.path
                                                ? "text-gold"
                                                : "text-white/90"
                                        }`}
                                        initial={{ y: "110%" }}
                                        animate={{ y: 0 }}
                                        exit={{ y: "110%" }}
                                        transition={{
                                            delay: 0.06 * i,
                                            duration: 0.7,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        {link.label}
                                    </motion.button>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
